/**
 * DateRangePicker - Reusable date range selector with presets
 *
 * Features:
 * - Preset options (week, month, 3mo, 6mo, year, all)
 * - Dropdown with smooth animation
 * - Performance optimized with memoization
 */

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { formatMonthYearShort } from '@/utils/format';
import { useLanguage, t as i18nT } from '@/i18n';
import './DateRangePicker.css';

// ============================================================
// Types
// ============================================================

export type DateRangePreset =
  | 'week'
  | '2weeks'
  | 'month'
  | '2months'
  | '3months'
  | '6months'
  | 'year'
  | 'ytd'
  | 'all';

export interface DateRange {
  start: Date;
  end: Date;
  preset: DateRangePreset;
  label: string;
}

interface DateRangePickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  className?: string;
}

// ============================================================
// Constants & Storage
// ============================================================

const STORAGE_KEY = 'ninjacash_analytics_daterange';

/**
 * Save selected preset to localStorage.
 */
export function saveDateRangePreset(preset: DateRangePreset): void {
  try {
    localStorage.setItem(STORAGE_KEY, preset);
  } catch {
    // Ignore storage errors
  }
}

/**
 * Load saved preset from localStorage.
 */
export function loadDateRangePreset(): DateRangePreset {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && isValidPreset(saved)) {
      return saved as DateRangePreset;
    }
  } catch {
    // Ignore storage errors
  }
  return '6months'; // Default
}

function isValidPreset(value: string): boolean {
  return ['week', '2weeks', 'month', '2months', '3months', '6months', 'year', 'ytd', 'all'].includes(value);
}

// ============================================================
// Helpers
// ============================================================

// Labels are catalog keys, resolved with t() where displayed (preset values
// are stable machine IDs and never change).
const PRESET_CONFIG: Record<DateRangePreset, { label: string; shortLabel: string }> = {
  week: { label: 'ranges.week', shortLabel: 'ranges.week_short' },
  '2weeks': { label: 'ranges.weeks2', shortLabel: 'ranges.weeks2_short' },
  month: { label: 'ranges.month', shortLabel: 'ranges.month_short' },
  '2months': { label: 'ranges.months2', shortLabel: 'ranges.months2_short' },
  '3months': { label: 'ranges.months3', shortLabel: 'ranges.months3_short' },
  '6months': { label: 'ranges.months6', shortLabel: 'ranges.months6_short' },
  year: { label: 'ranges.year', shortLabel: 'ranges.year_short' },
  ytd: { label: 'ranges.ytd', shortLabel: 'ranges.ytd_short' },
  all: { label: 'ranges.all', shortLabel: 'ranges.all_short' },
};

/**
 * Calculate date range for a preset.
 * Performance: O(1) - just date arithmetic, no DB calls.
 */
export function calculateDateRange(preset: DateRangePreset): DateRange {
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
  let start: Date;

  switch (preset) {
    case 'week':
      start = new Date(end);
      start.setDate(start.getDate() - 6);
      start.setHours(0, 0, 0, 0);
      break;
    case '2weeks':
      start = new Date(end);
      start.setDate(start.getDate() - 13);
      start.setHours(0, 0, 0, 0);
      break;
    case 'month':
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      break;
    case '2months':
      start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      break;
    case '3months':
      start = new Date(now.getFullYear(), now.getMonth() - 2, 1);
      break;
    case '6months':
      start = new Date(now.getFullYear(), now.getMonth() - 5, 1);
      break;
    case 'year':
      start = new Date(now.getFullYear(), now.getMonth() - 11, 1);
      break;
    case 'ytd':
      start = new Date(now.getFullYear(), 0, 1);
      break;
    case 'all':
      start = new Date(2000, 0, 1); // Far past date
      break;
    default:
      start = new Date(now.getFullYear(), now.getMonth() - 5, 1);
  }

  return {
    start,
    end,
    preset,
    label: i18nT(PRESET_CONFIG[preset].label),
  };
}

/**
 * Get display label for current range.
 */
function getDisplayLabel(range: DateRange): string {
  const now = new Date();
  const isCurrentMonth =
    range.start.getMonth() === now.getMonth() &&
    range.start.getFullYear() === now.getFullYear();

  if (range.preset === 'month' && isCurrentMonth) {
    return formatMonthYearShort(now);
  }

  if (range.preset === 'all') {
    return i18nT('ranges.all');
  }

  return i18nT(PRESET_CONFIG[range.preset].label);
}

// ============================================================
// Component
// ============================================================

export function DateRangePicker({ value, onChange, className = '' }: DateRangePickerProps) {
  const { language } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Close on escape
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  const handleSelect = useCallback(
    (preset: DateRangePreset) => {
      const newRange = calculateDateRange(preset);
      onChange(newRange);
      setIsOpen(false);
    },
    [onChange]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const displayLabel = useMemo(() => getDisplayLabel(value), [value, language]);

  const presets = useMemo(
    () =>
      (Object.keys(PRESET_CONFIG) as DateRangePreset[]).map((preset) => ({
        preset,
        label: i18nT(PRESET_CONFIG[preset].label),
        shortLabel: i18nT(PRESET_CONFIG[preset].shortLabel),
        isActive: value.preset === preset,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value.preset, language]
  );

  return (
    <div className={`date-range-picker ${className}`} ref={dropdownRef}>
      <button
        className="date-range-btn"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span className="date-range-icon">📅</span>
        <span className="date-range-label">{displayLabel}</span>
        <span className={`date-range-arrow ${isOpen ? 'open' : ''}`}>▼</span>
      </button>

      {isOpen && (
        <div className="date-range-dropdown" role="listbox">
          <div className="date-range-presets">
            {presets.map(({ preset, label, isActive }) => (
              <button
                key={preset}
                className={`date-range-option ${isActive ? 'active' : ''}`}
                onClick={() => handleSelect(preset)}
                role="option"
                aria-selected={isActive}
              >
                {label}
                {isActive && <span className="option-check">✓</span>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default DateRangePicker;
